/**
 * Chat page - Welcome screen for starting new conversations
 *
 * Now uses global ContentContextProvider for context management.
 * Context persists when navigating to Write page.
 */

import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks';
import { useUser } from '@/contexts/UserContext';
import { useContentContext } from '@/contexts/ContentContextProvider';
import { toChatContext } from '@/types/context';
import {
  FileText,
  Mail,
  Linkedin,
  ArrowRight,
  MessageSquare,
  Clock,
  Trash2,
  Lightbulb,
  Asterisk,
  Earth,
  ArrowUp
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  sendChatMessage,
  getConversations,
  getLastResponseId,
  archiveConversation,
  type ChatMessage as ChatMessageType,
  type ChatConversation,
  type ChatResponse
} from '@/api/chat';
import { supabase } from '@/integrations/supabase/client';
import { CompactContextBar } from '@/components/chat/CompactContextBar';
import { TypingIndicator } from '@/components/chat/ChatMessage';
import { AuthDialog } from '@/components/auth/AuthDialog';
import { WritingStylePromptDialog } from '@/components/dialogs/WritingStylePromptDialog';
import { motion } from 'framer-motion';
import Testimonial from '@/components/marketing/Testimonial';
import { Badge } from '@/components/ui/badge';
import { PricingGrid } from '@/components/subscription/PricingGrid';
import { SubscriptionPaywall } from '@/components/subscription/SubscriptionPaywall';

const PENDING_CHAT_INPUT_KEY = 'arcitext:pending-chat-input';

export default function Chat() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useUser();
  const [searchParams] = useSearchParams();

  // Global context
  const { effectiveContext, clearContext } = useContentContext();

  // Auth dialog state
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');

  // Writing style prompt dialog state
  const [writingStylePromptOpen, setWritingStylePromptOpen] = useState(false);

  // State
  const conversationIdFromUrl = searchParams.get('conversation') || undefined;
  const [conversationId, setConversationId] = useState<string | undefined>(conversationIdFromUrl);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [lastCredits, setLastCredits] = useState<ChatResponse['credits'] | null>(null);
  const [userCredits, setUserCredits] = useState<number | null>(null);

  // Chat history state
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Track when first message is being sent (for loading on welcome screen)
  const [isFirstMessageSending, setIsFirstMessageSending] = useState(false);

  // Determine if we're in active chat mode - stay on welcome screen during first message
  const hasMessages = messages.length > 0 && !isFirstMessageSending;

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
    }
  }, [message]);

  // Clear context and load prefilled prompt on mount
  useEffect(() => {
    clearContext(); // Start fresh on Chat page

    // Check for prefilled prompt from marketing demo
    const MARKETING_CHAT_PROMPT_KEY = 'arcitext_marketing_chat_prompt';
    if (typeof window !== 'undefined') {
      try {
        const pendingInput = localStorage.getItem(PENDING_CHAT_INPUT_KEY);
        if (pendingInput) {
          setMessage(pendingInput);
          localStorage.removeItem(PENDING_CHAT_INPUT_KEY);
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);
          return;
        }

        const prefilledPrompt = sessionStorage.getItem(MARKETING_CHAT_PROMPT_KEY);
        if (prefilledPrompt) {
          setMessage(prefilledPrompt);
          sessionStorage.removeItem(MARKETING_CHAT_PROMPT_KEY); // Clear after reading
          // Focus the textarea after a short delay to ensure it's rendered
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);
        }
      } catch (err) {
        console.warn('Failed to read stored chat input:', err);
      }
    }
  }, [clearContext]);

  // Persist unauthenticated draft so it survives auth redirects.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (user) return;
    try {
      if (message.trim().length > 0) {
        localStorage.setItem(PENDING_CHAT_INPUT_KEY, message);
      } else {
        localStorage.removeItem(PENDING_CHAT_INPUT_KEY);
      }
    } catch (err) {
      console.warn('Failed to store chat input draft:', err);
    }
  }, [message, user]);

  // Clean up persisted draft once authenticated.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user) return;
    try {
      localStorage.removeItem(PENDING_CHAT_INPUT_KEY);
    } catch (err) {
      console.warn('Failed to clear stored chat input:', err);
    }
  }, [user]);

  // Open auth dialog / prefill prompt via query params, then clean them from the URL
  useEffect(() => {
    const authParam = searchParams.get('auth');
    const promptParam = searchParams.get('prompt');
    let shouldReplaceUrl = false;

    if (authParam === 'signup' || authParam === 'signin') {
      setAuthMode(authParam);
      setAuthOpen(true);
      shouldReplaceUrl = true;
    }

    if (promptParam) {
      setMessage(promptParam);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      shouldReplaceUrl = true;
    }

    if (shouldReplaceUrl) {
      const params = new URLSearchParams(searchParams);
      params.delete('auth');
      params.delete('prompt');

      const nextSearch = params.toString();
      navigate(
        {
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true }
      );
    }
  }, [searchParams, navigate]);

  // Close auth dialog automatically after successful sign-in
  useEffect(() => {
    if (user && authOpen) {
      setAuthOpen(false);
    }
  }, [user, authOpen]);

  // Load history/credits only when authenticated
  useEffect(() => {
    if (!user) {
      setConversations([]);
      setUserCredits(null);
      return;
    }
    loadHistory();
    loadUserCredits();
  }, [user]);

  // Redirect to Write page when conversation has messages (fallback)
  useEffect(() => {
    if (hasMessages && conversationId) {
      navigate(`/app/write?conversation=${conversationId}`);
    }
  }, [hasMessages, conversationId, navigate]);

  // Fetch user credits from profile
  const loadUserCredits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('free_credits')
        .eq('user_id', user.id)
        .single();

      if (!error && data) {
        setUserCredits(data.free_credits ?? 0);
      }
    } catch (err) {
      console.error('Failed to load credits:', err);
    }
  };

  const hasWritingStyle = !!(effectiveContext.writingStyleId || effectiveContext.brandProfileId);
  const isPaidTier = !!profile && ['weekly', 'solo', 'pro', 'pro_byok'].includes(profile.subscription_tier);
  const isPaidOrTrialingStatus = profile?.subscription_status === 'active' || profile?.subscription_status === 'trialing';
  const hasChatAccess = !user || (!!profile && (
    profile.is_founders_club ||
    profile.subscription_tier === 'pro_byok' ||
    (isPaidTier && isPaidOrTrialingStatus)
  ));
  const showHardPaywall = !!user && !profileLoading && !hasChatAccess;

  const getWritingStylePromptKey = () => {
    if (!user) return null;
    return `arcitext:writing-style-prompt:${user.id}`;
  };

  const shouldPromptWritingStyle = () => {
    if (!user || hasWritingStyle) return false;
    if (typeof window === 'undefined') return false;
    const key = getWritingStylePromptKey();
    if (!key) return false;
    return localStorage.getItem(key) !== 'dismissed';
  };

  const markWritingStylePromptDismissed = () => {
    if (typeof window === 'undefined') return;
    const key = getWritingStylePromptKey();
    if (key) {
      localStorage.setItem(key, 'dismissed');
    }
  };

  const sendMessage = async (trimmedMessage: string) => {
    setIsLoading(true);
    setMessage('');

    // Optimistically add user message
    const tempUserMessage: ChatMessageType = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId || '',
      user_id: '',
      role: 'user',
      content: trimmedMessage,
      has_draft: false,
      used_web_search: false,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMessage]);

    // Track if this is a new conversation (for navigation after success)
    const isNewConversation = !conversationId;

    // If new conversation, show loading on welcome screen
    if (isNewConversation) {
      setIsFirstMessageSending(true);
    }

    try {
      let previousResponseId: string | undefined;
      if (conversationId) {
        previousResponseId = (await getLastResponseId(conversationId)) || undefined;
      }

      // Convert global context to ChatContext format for API
      const chatContext = toChatContext(effectiveContext);

      const response = await sendChatMessage({
        message: trimmedMessage,
        conversation_id: conversationId,
        previous_response_id: previousResponseId,
        context: chatContext,
      });

      // If this was a new conversation, redirect to Write page
      if (isNewConversation && response.conversation_id) {
        // Store conversation ID in sessionStorage for Write page to pick up
        sessionStorage.setItem('chat_conversation_id', response.conversation_id);

        // If a content piece was created (draft generated), navigate to it directly
        if (response.content_piece_id) {
          navigate(`/app/write/${response.content_piece_id}?conversation=${response.conversation_id}`);
        } else {
          // No draft, just open Write with conversation
          navigate(`/app/write?conversation=${response.conversation_id}`);
        }
        return;
      }

      if (!conversationId) {
        setConversationId(response.conversation_id);
      }

      const assistantMessage: ChatMessageType = {
        id: response.message_id,
        conversation_id: response.conversation_id,
        user_id: '',
        role: 'assistant',
        content: response.content,
        openai_response_id: response.response_id,
        has_draft: response.has_draft,
        draft_content: response.draft_content,
        credits_consumed: response.credits.consumed,
        total_tokens: response.credits.tokens.total,
        used_web_search: response.used_web_search,
        created_at: new Date().toISOString(),
      };

      setMessages(prev => [
        ...prev.filter(m => !m.id.startsWith('temp-')),
        { ...tempUserMessage, conversation_id: response.conversation_id },
        assistantMessage,
      ]);

      setLastCredits(response.credits);
      // Also update userCredits with the latest value
      if (response.credits?.remaining?.free !== undefined) {
        setUserCredits(response.credits.remaining.free);
      }

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      console.error('Failed to send message:', err);
      setMessages(prev => prev.filter(m => !m.id.startsWith('temp-')));
      setIsFirstMessageSending(false);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsFirstMessageSending(false);
    }
  };

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isLoading || authLoading || profileLoading) return;

    if (!user) {
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(PENDING_CHAT_INPUT_KEY, message);
        } catch (err) {
          console.warn('Failed to store chat input draft:', err);
        }
      }
      setAuthMode('signup');
      setAuthOpen(true);
      return;
    }

    if (showHardPaywall) {
      toast({
        title: 'Upgrade required',
        description: 'Choose a plan to start chatting with Arcitext.',
        variant: 'destructive',
      });
      return;
    }

    if (shouldPromptWritingStyle()) {
      setWritingStylePromptOpen(true);
      return;
    }

    await sendMessage(trimmedMessage);
  };

  const handleSkipWritingStyle = () => {
    markWritingStylePromptDismissed();
    setWritingStylePromptOpen(false);
    const trimmedMessage = message.trim();
    if (trimmedMessage) {
      sendMessage(trimmedMessage);
    }
  };

  const handleCreateWritingStyle = () => {
    markWritingStylePromptDismissed();
    setWritingStylePromptOpen(false);
    navigate('/app/writing-styles/create');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (prompt: string) => {
    setMessage(prompt);
    textareaRef.current?.focus();
  };

  // Load chat history
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const convos = await getConversations();
      setConversations(convos);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const selectConversation = (conv: ChatConversation) => {
    // Navigate directly to Write page with the conversation
    navigate(`/app/write?conversation=${conv.id}`);
  };

  const handleArchiveConversation = async (convId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await archiveConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        startNewConversation();
      }
      toast({ title: 'Conversation archived' });
    } catch (err) {
      console.error('Failed to archive:', err);
    }
  };

  const startNewConversation = () => {
    setConversationId(undefined);
    setMessages([]);
    clearContext(); // Clear global context for new conversation
    setLastCredits(null);
  };

  const canSend = hasChatAccess && message.trim().length > 0 && !isLoading;
  const displayName = profile?.display_name?.split(' ')[0] || 'stranger';

  // Format relative time (e.g., "2 hours ago", "yesterday")
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // ============================================================
  // WELCOME STATE - Centered input with suggestions and history
  // ============================================================
  if (!hasMessages) {
    return (
      <>
        <div className="min-h-screen flex flex-col">
          {/* Main content - centered */}
          <div className="flex-1 flex flex-col items-center justify-start sm:justify-center px-4 pt-36 pb-32 sm:pt-0 sm:pb-0">
            <div className="w-full flex flex-col justify-start sm:justify-center min-h-0 sm:min-h-[80vh] max-w-3xl mx-auto">
              {/* Welcome message */}
              {!user && (
                <div className="flex flex-col space-y-4 mb-8 items-center">
                  {/* Badge - H1 for SEO */}
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase"
                  >
                    Turn Thoughts Into Signal
                  </motion.h1>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
                    className="text-5xl sm:text-5xl lg:text-7xl font-semibold text-center mb-6 leading-tight"
                  >
                    The Writing Copilot
                    <span className="text-primary block font-serif italic"> for Founders</span>
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
                    className="text-center text-muted-foreground mb-8 text-base sm:text-xl"
                  >
                    Your voice is leverage. Draft faster. Build authority with founder-led content.
                  </motion.p>
                </div>
              )}
              {user && (
                <>
                  <h1 className="text-2xl sm:text-3xl font-semibold text-center mb-2">
                    {showHardPaywall ? `Welcome, ${displayName}.` : `Let's write something, ${displayName}.`}
                  </h1>
                  <p className="text-center text-muted-foreground mb-8 text-sm sm:text-base">
                    {showHardPaywall
                      ? 'Choose a plan to unlock chat and AI writing features.'
                      : 'Ask Arcitext to create a draft, research topics, or refine your text.'}
                  </p>
                </>
              )}

              {/* Centered input */}
              {!user ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
                  className="bg-background rounded-2xl p-3 sm:p-5 shadow-lg w-full"
                >
                  <div className="relative pr-16">
                    <Textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Describe what you want to write, get ideas or research topics..."
                      disabled={isLoading}
                      className={cn(
                        "min-h-[56px] resize-none pr-14 text-sm sm:text-base",
                        "rounded-2xl scrollbar-thin scrollbar-thumb-muted-foreground/50 scrollbar-track-background border-none",
                        "py-4 px-5 bg-background border-none",
                        "focus-visible:ring-0 focus-visible:ring-transparent focus-visible:shadow-none shadow-none"
                      )}
                      rows={1}
                    />
                    <Button
                      size="icon"
                      onClick={handleSend}
                      disabled={!canSend}
                      className={cn(
                        "absolute right-2 top-2 h-10 w-10 rounded-full",
                        canSend && "bg-primary hover:bg-primary/90"
                      )}
                    >
                      {isLoading ? (
                        <Asterisk className="h-5 w-5 animate-spin" />
                      ) : (
                        <ArrowRight className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                  {/* Context bar (writing style selector) */}
                  <div className="mt-2">
                    {isFirstMessageSending ? (
                      <div className="text-center max-w-md">
                        <TypingIndicator />
                      </div>
                    ) : (
                      <CompactContextBar
                        disabled={isLoading}
                        requireAuth={!user}
                        onRequireAuth={() => {
                          setAuthMode('signup');
                          setAuthOpen(true);
                        }}
                        collapseOnMobile
                      />
                    )}
                  </div>
                </motion.div>
              ) : showHardPaywall ? (
                <div className="bg-background rounded-2xl p-4 sm:p-6 shadow-lg w-full">
                  <p className="text-sm text-center text-muted-foreground">
                    Start your free trial to unlock chat.
                  </p>
                </div>
              ) : (
                <div className="bg-background rounded-2xl p-3 sm:p-5 shadow-lg w-full">
                  <div className="relative pr-16">
                    <Textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Describe what you want to write, get ideas or research topics..."
                      disabled={isLoading}
                      className={cn(
                        "min-h-[56px] resize-none pr-14 text-sm sm:text-base",
                        "rounded-2xl scrollbar-thin scrollbar-thumb-muted-foreground/50 scrollbar-track-background border-none",
                        "py-4 px-5 bg-background border-none",
                        "focus-visible:ring-0 focus-visible:ring-transparent focus-visible:shadow-none shadow-none"
                      )}
                      rows={1}
                    />
                    <Button
                      size="icon"
                      onClick={handleSend}
                      disabled={!canSend}
                      className={cn(
                        "absolute right-2 top-2 h-10 w-10 rounded-full",
                        canSend && "bg-primary hover:bg-primary/90"
                      )}
                    >
                      {isLoading ? (
                        <Asterisk className="h-5 w-5 animate-spin" />
                      ) : (
                        <ArrowUp className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                  {/* Context bar (writing style selector) */}
                  <div className="mt-2">
                    {isFirstMessageSending ? (
                      <div className="text-center max-w-md">
                        <TypingIndicator />
                      </div>
                    ) : (
                      <CompactContextBar
                        disabled={isLoading}
                        requireAuth={!user}
                        onRequireAuth={() => {
                          setAuthMode('signup');
                          setAuthOpen(true);
                        }}
                        collapseOnMobile
                      />
                    )}
                  </div>
                </div>
              )}
              {!user && (
                <motion.div className="mt-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}>
                  <p className="text-sm text-center text-muted-foreground">
                    Try Arcitext Free For 7 Days • Cancel Anytime
                  </p>
                </motion.div>
              )}
            </div>
          </div>

          {/* Recent conversations - bottom section */}
          {conversations.length > 0 && (
            <div className="border-t bg-muted/30 px-4 sm:px-6 py-5">
              <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-muted-foreground">Recent conversations</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {conversations.slice(0, 6).map((conv, index) => (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={cn(
                        "w-full p-3 sm:p-4 rounded-xl border bg-background",
                        "text-left transition-all hover:border-primary/50 hover:shadow-md",
                        "group",
                        index > 1 && "hidden sm:block"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <MessageSquare className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity -mr-1 -mt-1"
                          onClick={(e) => handleArchiveConversation(conv.id, e)}
                        >
                          <Trash2 className="h-3 w-3 text-muted-foreground" />
                        </Button>
                      </div>
                      <p className="font-medium text-sm truncate mb-1">
                        {conv.title || 'Untitled conversation'}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatRelativeTime(conv.updated_at)}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!user &&
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
              >
                <Testimonial className="max-w-3xl mb-24 mx-auto" quote="Whether I'm editing an ebook, writing an article, a blog post, a tweet, etc. I tend to reach for Arcitext because it's that good. It rivals other mature products I've used in the past." author="Jon C. Phillips" link="https://joncphillips.com" linkText="Visit" trustBadge={<span className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase"><Earth className="mr-1 h-4 w-4" />Trusted by 300+ Writers Around The World</span>} />
              </motion.div>

              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
                className="w-full max-w-3xl mx-auto px-4 mb-24"
              >
                <div className="text-center mb-10">
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
                    className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase"
                  >
                    Your Writing Workflow, Improved
                  </motion.span>
                  <h2 className="text-2xl sm:text-4xl font-semibold mt-4">
                    From Slow Drafts to Confident Writing
                  </h2>
                  <p className="text-muted-foreground text-base sm:text-lg mt-3">
                    Arcitext keeps you in control while removing the busywork that drains your momentum.
                  </p>
                </div>



                <div className="space-y-6">
                  <div className="rounded-2xl border bg-background/70 p-6 shadow-sm">
                    <p className="text-xs uppercase font-bold text-destructive">Without Arcitext</p>
                    <h3 className="text-lg font-semibold mt-2">LLMs output texts in their entirety</h3>
                    <p className="text-base text-muted-foreground mt-2">
                      You loose context, because you get a new version of your text every time. Let's not even go into hallucinations.
                    </p>
                    <div className="mt-4 border-t border-border/50 pt-4">
                      <p className="text-xs uppercase font-bold text-primary">With Arcitext</p>
                      <p className="text-base mt-2">
                        You chat with a living document. Arcitext suggests changes, gives you feedback, and grammar checks. You to accept or reject revisions as you please. Much like Cursor.
                      </p>
                    </div>
                  </div>
                  <div className="rounded-2xl border bg-background/70 p-6 shadow-sm">
                    <p className="text-xs uppercase font-bold text-destructive">Without Arcitext</p>
                    <h3 className="text-lg font-semibold mt-2">Writing and researching manually is slow</h3>
                    <p className="text-base text-muted-foreground mt-2">
                      You stare at a blank page, look for angles, and lose hours before the first draft even exists.
                    </p>
                    <div className="mt-4 border-t border-border/50 pt-4">
                      <p className="text-xs uppercase font-bold text-primary">With Arcitext</p>
                      <p className="text-base mt-2">
                        Discover and research trends. Kickstart momentum with drafts that fit your voice, audience, purpose, and channel. Like Lovable for writing.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border bg-background/70 p-6 shadow-sm">
                    <p className="text-xs uppercase font-bold text-destructive">Without Arcitext</p>
                    <h3 className="text-lg font-semibold mt-2">Letting AI write everything sounds synthetic</h3>
                    <p className="text-base text-muted-foreground mt-2">
                      Generic output breaks your voice, and you end up rewriting most of it anyway.
                    </p>
                    <div className="mt-4 border-t border-border/50 pt-4">
                      <p className="text-xs uppercase font-bold text-primary">With Arcitext</p>
                      <p className="text-base mt-2">
                        Lock in your writing DNA, purpose, and audience so your writing stays on‑brand and under your control.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border bg-background/70 p-6 shadow-sm">
                    <p className="text-xs uppercase font-bold text-destructive">Without Arcitext</p>
                    <h3 className="text-lg font-semibold mt-2">Endless iterations drain confidence</h3>
                    <p className="text-base text-muted-foreground mt-2">
                      You second‑guess every sentence and lose time finding the right words.
                    </p>
                    <div className="mt-4 border-t border-border/50 pt-4">
                      <p className="text-xs uppercase font-bold text-primary">With Arcitext</p>
                      <p className="text-base mt-2">
                        Selective tone‑fit checks, rewrite suggestions, and fact‑checking help you ship with clarity and confidence.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border bg-background/70 p-6 shadow-sm">
                    <p className="text-xs uppercase font-bold text-destructive">Without Arcitext</p>
                    <h3 className="text-lg font-semibold mt-2">Tool switching kills flow</h3>
                    <p className="text-base text-muted-foreground mt-2">
                      Research, drafting, and polishing happens in different apps. Your focus gets fragmented.
                    </p>
                    <div className="mt-4 border-t border-border/50 pt-4">
                      <p className="text-xs uppercase font-bold text-primary">With Arcitext</p>
                      <p className="text-base mt-2">
                        One workflow for discovery, drafting, and polishing means less time lost and more writing done.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.section>

              <div className="flex-1 space-y-12 flex flex-col items-center justify-center px-4">
                <div className="space-y-4 flex flex-col items-center justify-center px-4">
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
                    className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase"
                  >
                    Simple pricing, loads of usage
                  </motion.span>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7, ease: "easeOut" }}
                    className="text-3xl sm:text-5xl font-bold text-center mb-2 leading-tight"
                  >
                    Experience Better Writing. Today.
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8, ease: "easeOut" }}
                    className="text-center text-xl text-muted-foreground"
                  >
                    Save hours on text iterations, voice analysis, and exploration. Produce better founder-led content, faster.
                  </motion.p>
                </div>
                <PricingGrid />
              </div>
            </>
          }
        </div>

        <AuthDialog
          open={authOpen}
          onOpenChange={setAuthOpen}
          defaultMode={authMode}
        />
        <SubscriptionPaywall
          open={showHardPaywall}
          onOpenChange={() => { }}
          reason="Start your free trial or subscribe to begin chatting."
        />
        <WritingStylePromptDialog
          open={writingStylePromptOpen}
          onOpenChange={setWritingStylePromptOpen}
          onCreate={handleCreateWritingStyle}
          onSkip={handleSkipWritingStyle}
        />
      </>
    );
  }

  // Fallback: return null if somehow we have messages (redirect will happen via effect)
  return (
    <>
      <AuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        defaultMode={authMode}
      />
      <WritingStylePromptDialog
        open={writingStylePromptOpen}
        onOpenChange={setWritingStylePromptOpen}
        onCreate={handleCreateWritingStyle}
        onSkip={handleSkipWritingStyle}
      />
    </>
  );
}

// Quick action button component
function QuickActionButton({
  icon,
  label,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl w-full sm:w-auto",
        "border border-border bg-background shadow-sm",
        "text-sm font-medium",
        "hover:bg-muted hover:border-muted-foreground/30 transition-all",
        "hover:shadow-md"
      )}
    >
      {icon}
      {label}
    </button>
  );
}
